import {
  CreatePricingModelInput,
  PricingModelFragment,
  PricingModelFragmentDoc,
  UpdatePricingModelInput,
  useCreateGlobalPricingModelMutation,
  useCreatePricingModelMutation,
  useUpdatePricingModelMutation,
  useDeletePricingModelMutation,
  ProjectFragment,
} from '../generated/graphql'
import { useCacheHelpers } from '../apollo/cache-helpers'

type UsePricingModels = {
  isLoading: boolean

  createGlobalPricingModel(
    input: CreatePricingModelInput,
  ): Promise<PricingModelFragment>

  createPricingModel(
    projectId: ProjectFragment,
    input: CreatePricingModelInput,
  ): Promise<PricingModelFragment>

  updatePricingModel(
    id: string,
    input: UpdatePricingModelInput,
  ): Promise<PricingModelFragment>

  deletePricingModel(id: string, project?: ProjectFragment): Promise<void>
}

export function usePricingModels(): UsePricingModels {
  const [
    createGlobalPricingModelMutation,
    { loading: createGlobalPricingModelLoading },
  ] = useCreateGlobalPricingModelMutation()
  const [createPricingModelMutation, { loading: createPricingModelLoading }] =
    useCreatePricingModelMutation()
  const [updatePricingModelMutation, { loading: updatePricingModelLoading }] =
    useUpdatePricingModelMutation()
  const [deletePricingModelMutation, { loading: deletePricingModelLoading }] =
    useDeletePricingModelMutation()
  const {
    addItemToListInRootQuery,
    addItemToList,
    removeItemFromListInRootQuery,
    removeItemFromList,
  } = useCacheHelpers()

  const createGlobalPricingModel = async (input: CreatePricingModelInput) => {
    const result = await createGlobalPricingModelMutation({
      variables: {
        input,
      },

      update: (_, mutationResult) => {
        const pricingModel = mutationResult.data?.createGlobalPricingModel
        if (!pricingModel) {
          return
        }

        addItemToListInRootQuery(
          PricingModelFragmentDoc,
          pricingModel,
          'globalPricingModels',
        )
      },
    })

    if (!result.data) {
      throw new Error('Creating global pricing model failed')
    }

    return result.data.createGlobalPricingModel
  }

  const createPricingModel = async (
    project: ProjectFragment,
    input: CreatePricingModelInput,
  ) => {
    const result = await createPricingModelMutation({
      variables: {
        projectId: project.id,
        input,
      },

      update: (_, mutationResult) => {
        const pricingModel = mutationResult.data?.createPricingModel
        if (!pricingModel) {
          return
        }

        addItemToList(
          PricingModelFragmentDoc,
          pricingModel,
          project,
          'pricingModels',
        )
      },
    })

    if (!result.data) {
      throw new Error('Creating global pricing model failed')
    }

    return result.data.createPricingModel
  }

  const updatePricingModel = async (
    id: string,
    input: UpdatePricingModelInput,
  ) => {
    const result = await updatePricingModelMutation({
      variables: { id, input },
    })

    if (!result.data) {
      throw new Error('Updating pricing model failed')
    }
    return result.data.updatePricingModel
  }

  const deletePricingModel = async (id: string, project?: ProjectFragment) => {
    const result = await deletePricingModelMutation({
      variables: { id },
      optimisticResponse: {
        deletePricingModel: true,
      },
      update: (_, mutationResult) => {
        const deleteIsSuccess = mutationResult.data?.deletePricingModel
        if (!deleteIsSuccess) {
          return
        }

        if (project) {
          removeItemFromList(
            PricingModelFragmentDoc,
            id,
            project,
            'pricingModels',
          )
          return
        }
        removeItemFromListInRootQuery(
          PricingModelFragmentDoc,
          id,
          'globalPricingModels',
        )
      },
    })

    if (!result.data || !result.data.deletePricingModel) {
      throw new Error('Pricing model delete failed')
    }
  }

  return {
    isLoading:
      createGlobalPricingModelLoading ||
      createPricingModelLoading ||
      updatePricingModelLoading ||
      deletePricingModelLoading,

    createGlobalPricingModel,
    createPricingModel,
    updatePricingModel,
    deletePricingModel,
  }
}
